import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { errorMessage } from "@/lib/api";
import { datum } from "@/lib/format";
import { invoiceMoney, receivedInvoices, type InvoiceControl, type InvoiceOrderCandidate } from "@/lib/receivedInvoices";

interface Props {
  invoiceId: string;
  orders: InvoiceControl["orders"];
  editable: boolean;
  canOpen: boolean;
  onOpen: (id: string) => void;
  onChange: (kind: "link" | "unlink", id: string) => Promise<void>;
}

export function InvoiceOrderLinks({ invoiceId, orders, editable, canOpen, onOpen, onChange }: Props) {
  const [picker, setPicker] = useState(false);
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<InvoiceOrderCandidate[]>([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmUnlink, setConfirmUnlink] = useState("");

  useEffect(() => {
    if (!picker) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      receivedInvoices.offeredOrders(invoiceId, query, controller.signal)
        .then((result) => { if (!controller.signal.aborted) setRows(result); })
        .catch((cause) => {
          if (!controller.signal.aborted) setError(errorMessage(cause, "Narudžbe nisu učitane."));
        })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 200);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [invoiceId, picker, query]);

  return <div className="space-y-2">
    {editable && <Button onClick={() => {
      setPicker(true); setLoading(true); setError(""); setRows([]); setQuery(""); setSelected("");
    }} disabled={picker}>Poveži s narudžbom</Button>}
    {picker && <section aria-label="Povezivanje narudžbe" className="space-y-2 rounded-lg border p-3">
      <label className="block">
        Pretraga po NAR broju
        <input className="w-full rounded border p-2" value={query} disabled={!editable} maxLength={100}
          onChange={(event) => { setQuery(event.target.value); setSelected(""); setLoading(true); setError(""); }} />
      </label>
      {loading ? <p role="status">Učitavanje narudžbi…</p> : error ? <p role="alert">{error}</p> :
        rows.length ? <div className="max-h-64 space-y-1 overflow-y-auto">
          {rows.map((order) => <Button key={order.id} variant={selected === order.id ? "default" : "secondary"}
            className="h-auto min-h-11 w-full justify-start whitespace-normal text-left"
            aria-pressed={selected === order.id} disabled={!editable}
            onClick={() => setSelected(order.id)}>
            {order.broj} · {order.gradiliste} · {datum(order.datum)}{order.iznos !== null && ` · ${invoiceMoney(order.iznos)}`}
            {order.prijedlog && <span className="ml-2 rounded bg-amber-100 px-2 text-amber-950">Prijedlog</span>}
          </Button>)}
        </div> : <p>Nema narudžbi ovog dobavljača koje se mogu povezati.</p>}
      <div className="flex flex-wrap gap-2">
        <Button disabled={!editable || loading || !!error || !selected} onClick={() => {
          setPicker(false); void onChange("link", selected);
        }}>Poveži</Button>
        <Button variant="secondary" disabled={!editable} onClick={() => setPicker(false)}>Odustani</Button>
      </div>
    </section>}
    {orders.map((order) => <div key={order.id} className="flex flex-wrap items-center gap-2">
      {canOpen && <Button variant="secondary" onClick={() => onOpen(order.id)}>Otvori {order.broj || "narudžbu"}</Button>}
      {order.izvor === "minimax" && <span>Poveznica dolazi iz reference u Minimaxu; ispravite referencu u Minimaxu.</span>}
      {editable && order.izvor === "rucno" && (confirmUnlink === order.id ? <>
        <span>Odspojiti {order.broj || "narudžbu"}?</span>
        <Button variant="destructive" onClick={() => { setConfirmUnlink(""); void onChange("unlink", order.id); }}>Potvrdi odspajanje</Button>
        <Button variant="secondary" onClick={() => setConfirmUnlink("")}>Odustani</Button>
      </> : <Button variant="secondary" onClick={() => setConfirmUnlink(order.id)}>Odspoji</Button>)}
    </div>)}
  </div>;
}
